import { MeshAssembly } from '../bridge/MeshAssembly';
import { Component, MeshRenderer } from '../elements/component';
import { TransformPosition, TransformRotation } from '../interfaces';

export class Node {
	private _parent: Node | null;
	public children: Set<Node>;
	public components: Set<Component>;
	public isDestroyed = false;

	public set parent(parent: Node | null) {
		this.setParent(parent);
	}
	public get parent(): Node | null {
		return this._parent;
	}

	constructor(public assembly: MeshAssembly, public readonly id: string) {
		this.components = new Set<Component>();
		this.children = new Set<Node>();
	}

	/**
	 * Adds component of passed type.
	 */
	public addComponent<T extends Component>(c: new (n?: string) => T, name: string): T {
		const component: T = new c(name);
		this.components.add(component);
		return component;
	}

	/**
	 * Attaches component.
	 */
	public attachComponent(c: Component): void {
		this.components.add(c);
	}

	/**
	 * Detaches component .
	 */
	public detachComponent(c: Component): void {
		this.components.delete(c);
	}

	/**
	 * Gets component by name.
	 */
	public getComponent<T extends Component>(name: string): T {
		return Array.from(this.components).find((c) => c.name == name) as T;
	}

	/**
	 * Gets all child nodes, including deep children.
	 */
	public getAllChildNodes(): Array<Node> {
		return this.getDeepChildren();
	}

	/**
	 * Gets all parent nodes, including deep parent.
	 */
	public getAllParentNodes(): Array<Node> {
		return this.getDeepParents().reverse();
	}

	/**
	 * Gets all relative nodes.
	 */
	public getAllRelativeNodes(): Array<Node> {
		const children = this.getDeepChildren();
		const higherRelatives = this.getDeepParents(new Array<Node>(), true, this);
		const relatives = higherRelatives.concat(children);
		return relatives;
	}

	private getDeepChildren(acc = new Array<Node>(), ignore?: Node | null): Array<Node> {
		this.children.forEach((child) => {
			if (!ignore || ignore !== child) {
				acc.push(child);
				if (child.children.size > 0) {
					child.getDeepChildren(acc, ignore);
				}
			}
		});
		return acc;
	}

	private getDeepParents(
		acc = new Array<Node>(),
		includeChildren = false,
		ignore?: Node | null
	): Array<Node> {
		if (this._parent) {
			if (!ignore || ignore !== this._parent) {
				acc.push(this._parent);
				if (includeChildren) {
					this._parent.getDeepChildren(acc, ignore || null);
				}
				if (this._parent.parent) {
					this._parent.getDeepParents(acc, includeChildren, ignore ? ignore.parent : null);
				}
			}
		}
		return acc;
	}

	/**
	 * Sets absolute position of current node.
	 *
	 */
	public setAbsolutePosition(position: TransformPosition): void {
		this.assembly.worldPosition = position;
	}

	/**
	 * Sets local position of current node.
	 *
	 */
	public setLocalPosition(position: TransformPosition): void {
		this.assembly.localPosition = position;
	}

	/**
	 * Sets local rotation of current node.
	 *
	 */
	public setRotation(rotation: TransformRotation): void {
		this.assembly.localRotation = rotation;
	}

	/**
	 * Get local rotation of current node.
	 *
	 */
	public getRotation(): TransformRotation {
		return this.assembly.localRotation;
	}

	/**
	 * Localy translates mesh position. Is a test function and should be removed.
	 *
	 */
	public translate(x: number, y: number, z: number): void {
		this.assembly.translatePosition(x, y, z);
	}

	/**
	 * Sets visibility of current node.
	 *
	 */
	public set visibility(value: number) {
		this.components.forEach((c) => {
			if (c instanceof MeshRenderer) {
				c.state.visibility = value;
			}
		});
	}

	/**
	 * Sets passed mesh assembly as a child of current node.
	 *
	 */
	public addChildAssembly(child: MeshAssembly): void {
		// TODO rename to setParent() ?
		child.parent = this.assembly;
	}

	/**
	 * Connect current node as a child to a passsed node.
	 *
	 */
	public setParent(
		parent: Node | null,
		position?: TransformPosition,
		rotation?: TransformRotation
	): void {
		this.disconnect();
		this._parent = parent;
		if (parent) {
			parent.children.add(this);
			if (position) {
				this.assembly.localPosition = position;
			}
			if (rotation) {
				this.assembly.localRotation = rotation;
			}
			this.assembly.parent = parent.assembly;
		}
	}

	/**
	 * Disconnect current node.
	 *
	 */
	public disconnect(position?: TransformPosition, rotation?: TransformRotation): void {
		if (this._parent) {
			this._parent.children.delete(this);
			this._parent = null;
		}
		if (position) {
			this.assembly.localPosition = position;
		}
		if (rotation) {
			this.assembly.localRotation = rotation;
		}
		this.assembly.parent = null;
	}
}
